/**
 * GameTypeCard for Poker Reborn
 *
 * A lobby card representing a game type (Cash, Fast-Fold, SNG).
 * Contains Play Now (with expandable sub-options), Custom Play, and Watch buttons.
 *
 * @see docs/screens/lobby/GAME_TYPE_CARD.md - Visual specs, dimensions, behavior
 * @see src/config/lobbyCards.ts - Card descriptor data
 * @see src/components/lobby/PlayNowPanel.ts - Expandable Play Now sub-options
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button } from '@/components/common/Button';
import type { ButtonStyle } from '@/components/common/Button';
import { PlayNowPanel } from './PlayNowPanel';
import type { GameType } from '@/config/managers';

// =============================================================================
// TYPES
// =============================================================================

export interface GameTypeCardOptions {
  /** Which game type this card represents */
  gameType: GameType;
  /** Card title (e.g., "CASH GAMES") */
  title: string;
  /** Card subtitle (e.g., "No-Limit Hold'em") */
  subtitle: string;
  /** Card width */
  width: number;
  /** Card height */
  height: number;
}

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Card background */
  card: {
    color: 0x10101e,
    borderColor: 0x2a2a40,
    borderWidth: 2,
    cornerRadius: 12,
    padding: 16,
  },

  /** Title text */
  title: {
    fill: 0xffffff,
    fontSize: 18,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Subtitle text */
  subtitle: {
    fill: 0x7a7a9a,
    fontSize: 13,
    fontFamily: 'Roboto, Arial, sans-serif',
  },

  /** Play Now button (gold, primary) */
  playNow: {
    backgroundColor: 0xa48f17,
    hoverColor: 0xd4af37,
    pressColor: 0x846f07,
    borderColor: 0xd4af37,
    textColor: 0x000000,
    hoverGlowColor: 0xffd700,
    hoverGlowSize: 6,
    cornerRadius: 8,
    fontSize: 14,
  } satisfies Partial<ButtonStyle>,

  /** Custom Play button (green) */
  custom: {
    backgroundColor: 0x2a3a5e,
    hoverColor: 0x3a4a7e,
    pressColor: 0x1a2a4e,
    borderColor: 0x4a5a8e,
    textColor: 0xffffff,
    cornerRadius: 8,
    fontSize: 13,
  } satisfies Partial<ButtonStyle>,

  /** Layout gaps */
  gaps: {
    titleToSubtitle: 4,
    subtitleToPlayNow: 16,
    playNowToPanel: 6,
    panelToSecondary: 8,
    secondaryButtonGap: 8,
  },

  /** Button heights */
  buttonHeight: {
    playNow: 40,
    secondary: 34,
  },
};

// =============================================================================
// GAME TYPE CARD
// =============================================================================

export class GameTypeCard extends Container {
  private background: Graphics;
  private titleText: Text;
  private subtitleText: Text;
  private playNowButton: Button;
  private playNowPanel: PlayNowPanel;
  private customButton: Button;

  private lockOverlay: Container | null = null;
  private cardWidth: number;
  private cardHeight: number;
  private panelExpanded = false;

  /** Game type for this card */
  readonly gameType: GameType;

  // Callbacks
  onPlayNowSpectator?: () => void;
  onPlayNowAutoBuyIn?: () => void;
  onPlayNowAutoSeat?: () => void;
  onCustomPlay?: () => void;

  constructor(options: GameTypeCardOptions) {
    super();

    this.gameType = options.gameType;
    this.cardWidth = options.width;
    this.cardHeight = options.height;

    const padding = STYLES.card.padding;
    const contentWidth = options.width - padding * 2;

    // Background
    this.background = new Graphics();
    this.addChild(this.background);

    // Make card background interactive to stop propagation
    this.background.eventMode = 'static';

    // Title
    this.titleText = new Text({
      text: options.title,
      style: STYLES.title,
    });
    this.titleText.x = padding;
    this.titleText.y = padding;
    this.addChild(this.titleText);

    // Subtitle
    this.subtitleText = new Text({
      text: options.subtitle,
      style: STYLES.subtitle,
    });
    this.subtitleText.x = padding;
    this.subtitleText.y = padding + this.titleText.height + STYLES.gaps.titleToSubtitle;
    this.addChild(this.subtitleText);

    // Play Now button (full width)
    const playNowY = this.subtitleText.y + this.subtitleText.height + STYLES.gaps.subtitleToPlayNow;
    this.playNowButton = new Button({
      label: 'PLAY NOW',
      width: contentWidth,
      height: STYLES.buttonHeight.playNow,
      style: STYLES.playNow,
    });
    this.playNowButton.x = padding;
    this.playNowButton.y = playNowY;
    this.playNowButton.onClick = () => { this.togglePlayNowPanel(); };
    this.addChild(this.playNowButton);

    // Play Now Panel (hidden by default)
    this.playNowPanel = new PlayNowPanel(contentWidth);
    this.playNowPanel.x = padding;
    this.playNowPanel.y = playNowY + STYLES.buttonHeight.playNow + STYLES.gaps.playNowToPanel;
    this.playNowPanel.visible = false;
    this.playNowPanel.onSpectator = () => {
      this.collapsePlayNowPanel();
      this.onPlayNowSpectator?.();
    };
    this.playNowPanel.onAutoBuyIn = () => {
      this.collapsePlayNowPanel();
      this.onPlayNowAutoBuyIn?.();
    };
    this.playNowPanel.onAutoSeat = () => {
      this.collapsePlayNowPanel();
      this.onPlayNowAutoSeat?.();
    };
    this.addChild(this.playNowPanel);

    // Custom button (full width)
    const secondaryY = this.getSecondaryButtonsY();

    this.customButton = new Button({
      label: 'CUSTOM',
      width: contentWidth,
      height: STYLES.buttonHeight.secondary,
      style: STYLES.custom,
    });
    this.customButton.x = padding;
    this.customButton.y = secondaryY;
    this.customButton.onClick = () => this.onCustomPlay?.();
    this.addChild(this.customButton);

    // Draw background
    this.drawBackground();
  }

  /** Resize the card */
  resizeCard(width: number, height: number): void {
    this.cardWidth = width;
    this.cardHeight = height;

    const padding = STYLES.card.padding;
    const contentWidth = width - padding * 2;

    // Resize buttons
    this.playNowButton.resize(contentWidth, STYLES.buttonHeight.playNow);
    this.playNowPanel.resizeWidth(contentWidth);
    this.customButton.resize(contentWidth, STYLES.buttonHeight.secondary);

    const secondaryY = this.getSecondaryButtonsY();
    this.customButton.y = secondaryY;

    this.drawBackground();
  }

  /** Set a loading label on the Play Now button */
  setLoading(loading: boolean): void {
    this.playNowButton.disabled = loading;
    this.playNowButton.label = loading ? '...' : 'PLAY NOW';
  }

  /** Lock the card with a "Coming Soon" overlay. Disables all interaction. */
  setLocked(locked: boolean): void {
    if (locked) {
      // Disable all buttons
      this.playNowButton.disabled = true;
      this.customButton.disabled = true;
      this.collapsePlayNowPanel();

      // Dim the card content
      this.alpha = 0.5;

      // Create lock overlay if needed
      if (!this.lockOverlay) {
        this.lockOverlay = new Container();

        const lockLabel = new Text({
          text: 'COMING SOON',
          style: {
            fill: 0xffd700,
            fontSize: 14,
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 'bold',
          },
        });
        lockLabel.anchor.set(0.5);
        lockLabel.x = this.cardWidth / 2;
        lockLabel.y = this.cardHeight - 20;
        this.lockOverlay.addChild(lockLabel);
      }

      // Overlay is added on top but card alpha handles the dimming
      this.addChild(this.lockOverlay);
    } else {
      this.playNowButton.disabled = false;
      this.customButton.disabled = false;
      this.alpha = 1;

      if (this.lockOverlay) {
        this.removeChild(this.lockOverlay);
      }
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private togglePlayNowPanel(): void {
    this.panelExpanded = !this.panelExpanded;
    this.playNowPanel.visible = this.panelExpanded;

    // Reposition secondary button when panel opens/closes
    const secondaryY = this.getSecondaryButtonsY();
    this.customButton.y = secondaryY;

    this.drawBackground();
  }

  private collapsePlayNowPanel(): void {
    if (this.panelExpanded) {
      this.panelExpanded = false;
      this.playNowPanel.visible = false;

      const secondaryY = this.getSecondaryButtonsY();
      this.customButton.y = secondaryY;

      this.drawBackground();
    }
  }

  private getSecondaryButtonsY(): number {
    const playNowY = this.subtitleText.y + this.subtitleText.height + STYLES.gaps.subtitleToPlayNow;
    let afterPlayNow = playNowY + STYLES.buttonHeight.playNow;

    if (this.panelExpanded) {
      afterPlayNow += STYLES.gaps.playNowToPanel + this.playNowPanel.panelHeight;
    }

    return afterPlayNow + STYLES.gaps.panelToSecondary;
  }

  private drawBackground(): void {
    const { color, borderColor, borderWidth, cornerRadius } = STYLES.card;

    // Calculate actual height needed
    const secondaryBottom = this.getSecondaryButtonsY() + STYLES.buttonHeight.secondary + STYLES.card.padding;
    const actualHeight = Math.max(this.cardHeight, secondaryBottom);

    this.background.clear();

    // Card background
    this.background.roundRect(0, 0, this.cardWidth, actualHeight, cornerRadius);
    this.background.fill(color);

    // Border
    this.background.roundRect(0, 0, this.cardWidth, actualHeight, cornerRadius);
    this.background.stroke({ width: borderWidth, color: borderColor });
  }
}
